import 'what-input';

import React, { useEffect } from 'react';

import { Cursor } from '$partials/global/index.js';
import { setLocation } from '$utilities/location.js';
import { setUserAgent } from '$utilities/user-agent.js';
import { SkipLink } from '$partials/ui/index.js';
import * as Pages from '$pages/index.js';
import { ModalProvider } from '$contexts/modal.jsx';
import { CursorProvider } from '$contexts/cursor.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/app.d.ts').App}
 *
 * Renders the application.
 * 1. If the page component requested doesn’t exist, escapes the function.
 * 2. Skip link are useful for keyboard users to access the main content of the page
 *    straight away without the hassle of navigating through the navigation at each page load.
 * 3. Page component (HomePage, UnionPage, etc.).
 */
const App = props => {
    if (props.location) {
        setLocation(props.location);
    }

    if (props.userAgent) {
        setUserAgent(props.userAgent);
    }

    const handleBookViewingSubmit = (target) => {
        if (target) {
            if (window.dataLayer) {
                window.dataLayer.push({
                    event: 'book_viewing_submit',
                });
            }
        }
    };

    const handleBookValuationSubmit = (target) => {
        if (target) {
            if (window.dataLayer) {
                window.dataLayer.push({
                    event: 'book_valuation_submit'
                });
            }
        }
    };

    const handleNewsletterSignupSubmit = (target) => {
        if (target) {
            if (window.dataLayer) {
                window.dataLayer.push({
                    event: 'newsletter_signup_submit'
                });
            }
        }
    };

    const handleSubmit = (e) => {
        if (e?.target) {
            switch (e.target.getAttribute('data-analytics') ) {
                case 'book-viewing':
                    handleBookViewingSubmit(e.target);
                    break;
                case 'book-valuation':
                    handleBookValuationSubmit(e.target);
                    break;
                case 'newsletter-signup':
                    handleNewsletterSignupSubmit(e.target);
                    break;
                default:
                    break;
            }
        }
    };

    useEffect(() => {
        window.addEventListener('submit', handleSubmit, {passive: true});

        return () => {
            window.removeEventListener('submit', handleSubmit, {passive: true});
        };
    });

    /* [1] */
    if (!props.pageComponent || typeof (Pages[props.pageComponent]) === 'undefined') {
        return null;
    }

    const Component = Pages[props.pageComponent];
    return (
        <>
            <CursorProvider>
                <Cursor/>
                <ModalProvider>
                    <SkipLink href='#main-content'>Skip to main content</SkipLink> {/* [3] */}
                    <SkipLink href='#primary-navigation'>Skip to primary navigation</SkipLink> {/* [3] */}
                    <Component {...props} /> {/* [3] */}
                    {/* {props.dev && (<Grid />)} */}
                </ModalProvider>
            </CursorProvider>
        </>
    );
};

/* ========================================================================= */
/* Typing & export(s) */
/* ========================================================================= */

export { App };
